import React, { useState, useEffect } from 'react';
import { 
  collection, query, onSnapshot, addDoc, updateDoc, doc, getDoc, deleteDoc, 
  getDocs, where, serverTimestamp, orderBy 
} from 'firebase/firestore';
import { PlusCircle, Edit, Trash2, BookOpen, Settings, Save, X, Check, Link, Unlink, List, Grid, Globe, Search, Eye, EyeOff, AlertCircle } from 'lucide-react';

const MatieresManagement = ({ setMessage, setError, db, schoolId, userRole }) => {
  const [activeTab, setActiveTab] = useState('liste'); // 'ajouter', 'liste', 'liaisons', 'editer'
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- GESTION DES MATIERES DIRECTES ---
  const [matieresGlobales, setMatieresGlobales] = useState([]);
  const [filteredMatieres, setFilteredMatieres] = useState([]);
  const [newMatiereName, setNewMatiereName] = useState('');
  const [newMatiereDescription, setNewMatiereDescription] = useState('');
  const [newMatiereNiveau, setNewMatiereNiveau] = useState('tous');
  const [newMatiereType, setNewMatiereType] = useState('français');
  const [newMatiereStatus, setNewMatiereStatus] = useState('actif'); // 'actif', 'inactif'
  const [isEditingMatiere, setIsEditingMatiere] = useState(false);
  const [currentMatiereId, setCurrentMatiereId] = useState(null);
  
  // --- CLASSES ET LIAISONS ---
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classMatieres, setClassMatieres] = useState([]);
  const [coefficientInputs, setCoefficientInputs] = useState({});
  const [noteSurInputs, setNoteSurInputs] = useState({});
  
  // --- ÉDITION EN MASSE ---
  const [selectedMatieresForBulk, setSelectedMatieresForBulk] = useState([]);
  const [bulkEditNiveau, setBulkEditNiveau] = useState('');
  const [bulkEditType, setBulkEditType] = useState('');
  const [bulkEditStatus, setBulkEditStatus] = useState('');
  const [showBulkEditPanel, setShowBulkEditPanel] = useState(false);
  
  // --- NIVEAUX DISPONIBLES ---
  const niveauxOptions = [
    { value: 'tous', label: 'Tous les niveaux', icon: <Globe size={14} className="inline mr-1" /> },
    { value: 'préscolaire', label: 'Préscolaire' },
    { value: 'élémentaire', label: 'Élémentaire' },
    { value: 'collège', label: 'Collège' },
    { value: 'lycée', label: 'Lycée' }
  ];

  // --- TYPES DE MATIERES ---
  const typeOptions = [
    { value: 'français', label: 'Français' },
    { value: 'arabe', label: 'Arabe/Islamique' },
    { value: 'commun', label: 'Commun (Français & Arabe)' }
  ];

  // --- STATUTS ---
  const statusOptions = [
    { value: 'actif', label: 'Actif', color: 'bg-green-100 text-green-800' },
    { value: 'inactif', label: 'Inactif', color: 'bg-red-100 text-red-800' }
  ];

  const canManage = ['directeurPedagogique', 'secretaireGeneral', 'admin', 'superAdmin'].includes(userRole);

  // Fonction pour déterminer automatiquement le type de matière basé sur le nom
  const determineMatiereType = (matiereName) => {
    const matiereLower = matiereName.toLowerCase();
    
    const arabicSubjects = [
      'arabe', 'coran', 'islamique', 'islam', 'fiqh', 'aqida', 
      'tawhid', 'hadith', 'sira', 'théologie', 'religion',
      'quran', 'dini', 'islamiyyah', 'تعليم إسلامي', 'ديني',
      'قرآن', 'تجويد', 'فقه', 'عقيدة', 'حديث', 'سيرة'
    ];
    
    const commonSubjects = [
      'mathématiques', 'maths', 'sciences', 'physique', 'chimie', 
      'histoire', 'géographie', 'informatique', 'sport', 'éducation physique',
      'arts', 'dessin', 'musique', 'biologie', 'géologie'
    ];
    
    if (arabicSubjects.some(subject => matiereLower.includes(subject))) {
      return 'arabe';
    }
    
    if (commonSubjects.some(subject => matiereLower.includes(subject))) {
      return 'commun';
    }
    
    return 'français';
  };

  // 1. CHARGER LES MATIERES GLOBALES
  useEffect(() => {
    if (!db || !schoolId) return;

    setLoading(true);
    const q = query(
      collection(db, 'schools', schoolId, 'matieres_globales'),
      orderBy("name", "asc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const matieresData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setMatieresGlobales(matieresData);
      setFilteredMatieres(matieresData);
      setLoading(false);
    }, (error) => {
      console.error("Erreur chargement matières:", error);
      setError("Erreur lors du chargement des matières.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, schoolId, setError]);

  // 2. FILTRER LES MATIERES PAR RECHERCHE
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMatieres(matieresGlobales);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = matieresGlobales.filter(matiere => 
      matiere.name.toLowerCase().includes(term) ||
      (matiere.description && matiere.description.toLowerCase().includes(term)) ||
      matiere.niveau.toLowerCase().includes(term) ||
      matiere.type.toLowerCase().includes(term)
    );
    
    setFilteredMatieres(filtered);
  }, [searchTerm, matieresGlobales]);

  // 3. CHARGER LES CLASSES
  useEffect(() => {
    if (!db || !schoolId) return;

    const q = query(
      collection(db, 'schools', schoolId, 'classes'), 
      orderBy("nom", "asc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const classesData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setClasses(classesData);
    });

    return () => unsubscribe();
  }, [db, schoolId]);

  // 4. CHARGER LES LIAISONS POUR LA CLASSE SELECTIONNEE
  useEffect(() => {
    if (!db || !schoolId || !selectedClassId) {
      setClassMatieres([]);
      setCoefficientInputs({});
      setNoteSurInputs({});
      return;
    }

    const q = query(
      collection(db, 'schools', schoolId, 'class_matieres'),
      where('classId', '==', selectedClassId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const liaisonsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        serie: doc.data().serie || ''
      }));
      setClassMatieres(liaisonsData);
      
      const coeffInputs = {};
      const noteInputs = {};
      
      liaisonsData.forEach(liaison => {
        coeffInputs[liaison.id] = liaison.coefficient || 1;
        noteInputs[liaison.id] = liaison.noteSur || 20;
      });
      
      setCoefficientInputs(coeffInputs);
      setNoteSurInputs(noteInputs);
    });

    return () => unsubscribe();
  }, [db, schoolId, selectedClassId]);

  // --- FONCTIONS MATIERES ---
  const handleSaveMatiere = async (e) => {
    e.preventDefault();
    if (!newMatiereName.trim()) {
      setError("Veuillez remplir le nom de la matière.");
      return;
    }

    if (!schoolId) {
      setError("École non sélectionnée.");
      return;
    }

    try {
      const finalMatiereType = newMatiereType || determineMatiereType(newMatiereName);

      const matiereData = {
        name: newMatiereName.trim(),
        description: newMatiereDescription.trim(),
        niveau: newMatiereNiveau,
        type: finalMatiereType,
        status: newMatiereStatus,
        schoolId: schoolId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userRole || 'unknown'
      };

      if (isEditingMatiere && currentMatiereId) {
        // Vérifier si le nom a changé
        const oldMatiere = matieresGlobales.find(m => m.id === currentMatiereId);
        if (oldMatiere && oldMatiere.name !== newMatiereName.trim()) {
          // Vérifier si le nouveau nom existe déjà
          const exists = matieresGlobales.some(m => 
            m.name.toLowerCase() === newMatiereName.trim().toLowerCase() && 
            m.id !== currentMatiereId
          );
          
          if (exists) {
            setError(`Une matière "${newMatiereName}" existe déjà.`);
            return;
          }
        }

        await updateDoc(doc(db, 'schools', schoolId, 'matieres_globales', currentMatiereId), matiereData);
        setMessage("Matière modifiée avec succès !");
      } else {
        // Vérifier si la matière existe déjà
        const exists = matieresGlobales.some(m => 
          m.name.toLowerCase() === newMatiereName.trim().toLowerCase()
        );
        
        if (exists) {
          setError(`Une matière "${newMatiereName}" existe déjà.`);
          return;
        }

        await addDoc(collection(db, 'schools', schoolId, 'matieres_globales'), matiereData);
        setMessage("Matière ajoutée avec succès !");
      }

      resetMatiereForm();
    } catch (error) {
      console.error("Erreur sauvegarde matière:", error);
      setError("Erreur lors de la sauvegarde de la matière.");
    }
  };

  const handleEditMatiere = (matiere) => {
    setIsEditingMatiere(true);
    setCurrentMatiereId(matiere.id);
    setNewMatiereName(matiere.name);
    setNewMatiereDescription(matiere.description || '');
    setNewMatiereNiveau(matiere.niveau || 'tous');
    setNewMatiereType(matiere.type || 'français');
    setNewMatiereStatus(matiere.status || 'actif');
    setActiveTab('ajouter');
  };

  const handleDeleteMatiere = async (matiereId, matiereName) => {
    if (!window.confirm(`Supprimer définitivement la matière "${matiereName}" ?\n\nCette action est irréversible.`)) return;
    
    try {
      // Vérifier si la matière est utilisée dans des liaisons
      const q = query(
        collection(db, 'schools', schoolId, 'class_matieres'),
        where('matiereName', '==', matiereName),
        where('schoolId', '==', schoolId)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const classNames = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const liaison = doc.data();
            const classDoc = await getDoc(doc(db, 'schools', schoolId, 'classes', liaison.classId));
            return classDoc.exists() ? classDoc.data().nom : 'Classe inconnue';
          })
        );
        
        const message = `Cette matière est utilisée dans ${snapshot.size} classe(s):\n${classNames.join(', ')}\n\nSupprimez d'abord ces liaisons avant de supprimer la matière.`;
        setError(message);
        return;
      }

      // Vérifier si la matière est utilisée dans des notes
      const notesQuery = query(
        collection(db, 'schools', schoolId, 'notes'),
        where('matiereNom', '==', matiereName)
      );
      const notesSnapshot = await getDocs(notesQuery);
      
      if (!notesSnapshot.empty) {
        setError(`Cette matière est utilisée dans ${notesSnapshot.size} note(s). Supprimez d'abord ces notes.`);
        return;
      }

      await deleteDoc(doc(db, 'schools', schoolId, 'matieres_globales', matiereId));
      setMessage("Matière supprimée avec succès !");
    } catch (error) {
      console.error("Erreur suppression matière:", error);
      setError("Erreur lors de la suppression de la matière.");
    }
  };

  const handleToggleMatiereStatus = async (matiereId, currentStatus) => {
    const newStatus = currentStatus === 'actif' ? 'inactif' : 'actif';
    const action = newStatus === 'actif' ? 'réactiver' : 'désactiver';
    
    if (!window.confirm(`Voulez-vous ${action} cette matière ?\n\nLes matières inactives ne seront pas disponibles pour attribution aux classes.`)) return;
    
    try {
      await updateDoc(doc(db, 'schools', schoolId, 'matieres_globales', matiereId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      setMessage(`Matière ${action === 'désactiver' ? 'désactivée' : 'réactivée'} avec succès !`);
    } catch (error) {
      console.error("Erreur changement statut:", error);
      setError("Erreur lors du changement de statut.");
    }
  };

  const resetMatiereForm = () => {
    setIsEditingMatiere(false);
    setCurrentMatiereId(null);
    setNewMatiereName('');
    setNewMatiereDescription('');
    setNewMatiereNiveau('tous');
    setNewMatiereType('français');
    setNewMatiereStatus('actif');
  };

  // --- FONCTIONS ÉDITION EN MASSE ---
  const handleSelectMatiereForBulk = (matiereId) => {
    setSelectedMatieresForBulk(prev => {
      if (prev.includes(matiereId)) {
        return prev.filter(id => id !== matiereId);
      } else {
        return [...prev, matiereId];
      }
    });
  };

  const handleSelectAllMatieres = () => {
    if (selectedMatieresForBulk.length === filteredMatieres.length) {
      setSelectedMatieresForBulk([]);
    } else {
      setSelectedMatieresForBulk(filteredMatieres.map(m => m.id));
    }
  };

  const handleBulkEdit = async () => {
    if (selectedMatieresForBulk.length === 0) {
      setError("Veuillez sélectionner au moins une matière.");
      return;
    }

    const updates = {};
    let hasChanges = false;

    if (bulkEditNiveau) {
      updates.niveau = bulkEditNiveau;
      hasChanges = true;
    }
    
    if (bulkEditType) {
      updates.type = bulkEditType;
      hasChanges = true;
    }
    
    if (bulkEditStatus) {
      updates.status = bulkEditStatus;
      hasChanges = true;
    }

    if (!hasChanges) {
      setError("Veuillez sélectionner au moins un champ à modifier.");
      return;
    }

    if (!window.confirm(`Modifier ${selectedMatieresForBulk.length} matière(s) ?`)) return;

    try {
      const batchPromises = selectedMatieresForBulk.map(matiereId => 
        updateDoc(doc(db, 'schools', schoolId, 'matieres_globales', matiereId), {
          ...updates,
          updatedAt: serverTimestamp()
        })
      );

      await Promise.all(batchPromises);
      
      setMessage(`${selectedMatieresForBulk.length} matière(s) modifiée(s) avec succès !`);
      setSelectedMatieresForBulk([]);
      setBulkEditNiveau('');
      setBulkEditType('');
      setBulkEditStatus('');
      setShowBulkEditPanel(false);
    } catch (error) {
      console.error("Erreur édition en masse:", error);
      setError("Erreur lors de l'édition en masse.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMatieresForBulk.length === 0) {
      setError("Veuillez sélectionner au moins une matière.");
      return;
    }

    const matiereNames = selectedMatieresForBulk.map(id => {
      const matiere = matieresGlobales.find(m => m.id === id);
      return matiere ? matiere.name : 'Inconnue';
    }).join(', ');

    if (!window.confirm(`Supprimer définitivement ${selectedMatieresForBulk.length} matière(s) ?\n\n${matiereNames}\n\nCette action est irréversible.`)) return;

    try {
      // Vérifier si des matières sont utilisées
      const checkPromises = selectedMatieresForBulk.map(async (matiereId) => {
        const matiere = matieresGlobales.find(m => m.id === matiereId);
        if (!matiere) return { id: matiereId, canDelete: false, reason: 'Non trouvée' };

        const q = query(
          collection(db, 'schools', schoolId, 'class_matieres'),
          where('matiereName', '==', matiere.name)
        );
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          return { id: matiereId, canDelete: false, reason: 'Utilisée dans des classes' };
        }

        return { id: matiereId, canDelete: true };
      });

      const results = await Promise.all(checkPromises);
      const cannotDelete = results.filter(r => !r.canDelete);

      if (cannotDelete.length > 0) {
        const errorMessages = cannotDelete.map(r => 
          `• ${matieresGlobales.find(m => m.id === r.id)?.name}: ${r.reason}`
        ).join('\n');
        
        setError(`Les matières suivantes ne peuvent pas être supprimées :\n${errorMessages}`);
        return;
      }

      // Supprimer les matières
      const deletePromises = selectedMatieresForBulk.map(matiereId =>
        deleteDoc(doc(db, 'schools', schoolId, 'matieres_globales', matiereId))
      );

      await Promise.all(deletePromises);
      
      setMessage(`${selectedMatieresForBulk.length} matière(s) supprimée(s) avec succès !`);
      setSelectedMatieresForBulk([]);
    } catch (error) {
      console.error("Erreur suppression en masse:", error);
      setError("Erreur lors de la suppression en masse.");
    }
  };

  const handleBulkToggleStatus = async () => {
    if (selectedMatieresForBulk.length === 0) {
      setError("Veuillez sélectionner au moins une matière.");
      return;
    }

    const newStatus = bulkEditStatus || 'inactif';
    const action = newStatus === 'actif' ? 'réactiver' : 'désactiver';

    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${selectedMatieresForBulk.length} matière(s) ?`)) return;

    try {
      const batchPromises = selectedMatieresForBulk.map(matiereId =>
        updateDoc(doc(db, 'schools', schoolId, 'matieres_globales', matiereId), {
          status: newStatus,
          updatedAt: serverTimestamp()
        })
      );

      await Promise.all(batchPromises);
      
      setMessage(`${selectedMatieresForBulk.length} matière(s) ${action === 'désactiver' ? 'désactivée(s)' : 'réactivée(s)'} avec succès !`);
      setSelectedMatieresForBulk([]);
      setBulkEditStatus('');
    } catch (error) {
      console.error("Erreur changement statut en masse:", error);
      setError("Erreur lors du changement de statut en masse.");
    }
  };

  // --- FONCTIONS LIAISONS ---
  const handleAddMatiereToClass = async (matiere) => {
    if (!selectedClassId) {
      setError("Veuillez d'abord sélectionner une classe.");
      return;
    }

    if (!schoolId) {
      setError("École non sélectionnée.");
      return;
    }

    // Vérifier si la matière est inactive
    if (matiere.status === 'inactif') {
      setError("Cette matière est inactive et ne peut pas être attribuée à une classe.");
      return;
    }

    // Vérifier si la matière est déjà liée
    const alreadyLinked = classMatieres.some(cm => 
      cm.matiereId === matiere.id
    );
    
    if (alreadyLinked) {
      setError("Cette matière est déjà liée à cette classe.");
      return;
    }

    try {
      const selectedClass = classes.find(c => c.id === selectedClassId);
      const classSerie = selectedClass?.serie || '';
      
      const matiereType = matiere.type || determineMatiereType(matiere.name);

      const classeNiveau = selectedClass?.niveau || '';
      if (matiere.niveau !== 'tous' && matiere.niveau !== classeNiveau && classeNiveau !== '') {
        if (!window.confirm(`Cette matière est définie pour le niveau "${matiere.niveau}" mais la classe est "${classeNiveau}". Voulez-vous quand même l'ajouter ?`)) {
          return;
        }
      }

      await addDoc(collection(db, 'schools', schoolId, 'class_matieres'), {
        classId: selectedClassId,
        matiereName: matiere.name,
        matiereId: matiere.id,
        niveau: matiere.niveau || '',
        description: matiere.description || '',
        coefficient: 1,
        noteSur: 20,
        serie: classSerie,
        matiereType: matiereType,
        schoolId: schoolId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      setMessage(`Matière "${matiere.name}" ajoutée à la classe.`);
    } catch (error) {
      console.error("Erreur ajout matière à classe:", error);
      setError("Erreur lors de l'ajout de la matière.");
    }
  };

  const handleUpdateCoefficient = async (liaisonId, newValue) => {
    if (newValue < 0.5 || isNaN(newValue)) {
      setError("Le coefficient doit être un nombre d'au moins 0.5");
      return;
    }

    try {
      await updateDoc(doc(db, 'schools', schoolId, 'class_matieres', liaisonId), {
        coefficient: parseFloat(newValue),
        updatedAt: serverTimestamp()
      });
      
      setCoefficientInputs(prev => ({
        ...prev,
        [liaisonId]: newValue
      }));
    } catch (error) {
      console.error("Erreur mise à jour coefficient:", error);
      setError("Erreur lors de la mise à jour.");
    }
  };

  const handleUpdateNoteSur = async (liaisonId, newValue) => {
    if (newValue < 10 || isNaN(newValue)) {
      setError("La note sur doit être un nombre d'au moins 10");
      return;
    }

    try {
      await updateDoc(doc(db, 'schools', schoolId, 'class_matieres', liaisonId), {
        noteSur: parseInt(newValue),
        updatedAt: serverTimestamp()
      });
      
      setNoteSurInputs(prev => ({
        ...prev,
        [liaisonId]: newValue
      }));
    } catch (error) {
      console.error("Erreur mise à jour note sur:", error);
      setError("Erreur lors de la mise à jour.");
    }
  };

  const handleUpdateMatiereType = async (liaisonId, newType, matiereName) => {
    try {
      await updateDoc(doc(db, 'schools', schoolId, 'class_matieres', liaisonId), {
        matiereType: newType,
        updatedAt: serverTimestamp()
      });
      
      setMessage(`Type de matière "${matiereName}" mis à jour en "${newType === 'français' ? 'Français' : newType === 'arabe' ? 'Arabe' : 'Commun'}"`);
    } catch (error) {
      console.error("Erreur mise à jour type:", error);
      setError("Erreur lors de la mise à jour du type.");
    }
  };

  const handleRemoveLiaison = async (liaisonId, matiereName) => {
    if (!window.confirm(`Retirer la matière "${matiereName}" de cette classe ?`)) return;
    
    try {
      await deleteDoc(doc(db, 'schools', schoolId, 'class_matieres', liaisonId));
      setMessage("Matière retirée de la classe.");
    } catch (error) {
      console.error("Erreur suppression liaison:", error);
      setError("Erreur lors du retrait.");
    }
  };

  // Filtrer les matières par niveau et type
  const getFilteredMatieres = () => {
    if (!selectedClassId) return matieresGlobales;
    
    const selectedClass = classes.find(c => c.id === selectedClassId);
    const classNiveau = selectedClass?.niveau || '';
    
    return matieresGlobales.filter(matiere => 
      (matiere.niveau === 'tous' || matiere.niveau === classNiveau || classNiveau === '') &&
      matiere.status === 'actif' // Ne montrer que les matières actives
    );
  };

  // --- RENDU ---
  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
        <BookOpen className="mr-2" /> Gestion des Matières
        {schoolId && (
          <span className="ml-4 text-sm font-normal text-gray-600 bg-gray-100 px-3 py-1 rounded">
            École: {schoolId}
          </span>
        )}
      </h2>

      {!schoolId && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-700 font-medium">⚠️ Veuillez sélectionner une école pour gérer les matières.</p>
        </div>
      )}

      {/* Onglets */}
      <div className="flex border-b mb-6 overflow-x-auto">
        <button
          onClick={() => { setActiveTab('liste'); setSelectedMatieresForBulk([]); }}
          className={`px-6 py-3 font-medium whitespace-nowrap flex items-center ${
            activeTab === 'liste' 
              ? 'border-b-2 border-blue-600 text-blue-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <List className="mr-2" size={18} />
          Liste des Matières ({matieresGlobales.length})
          {selectedMatieresForBulk.length > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              {selectedMatieresForBulk.length} sélectionné(s)
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveTab('ajouter'); resetMatiereForm(); }}
          className={`px-6 py-3 font-medium whitespace-nowrap flex items-center ${
            activeTab === 'ajouter' 
              ? 'border-b-2 border-green-600 text-green-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <PlusCircle className="mr-2" size={18} />
          {isEditingMatiere ? 'Modifier une Matière' : 'Ajouter une Matière'}
        </button>
        <button
          onClick={() => setActiveTab('liaisons')}
          className={`px-6 py-3 font-medium whitespace-nowrap flex items-center ${
            activeTab === 'liaisons' 
              ? 'border-b-2 border-purple-600 text-purple-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Link className="mr-2" size={18} />
          Attribution aux Classes
        </button>
      </div>

      {/* ONGLET 1: LISTE DES MATIERES AVEC ÉDITION EN MASSE */}
      {activeTab === 'liste' && (
        <div className="space-y-6">
          {/* Barre de recherche et actions */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher une matière par nom, description, niveau ou type..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex space-x-2">
                {selectedMatieresForBulk.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowBulkEditPanel(!showBulkEditPanel)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm"
                    >
                      <Edit className="mr-2" size={16} />
                      Modifier ({selectedMatieresForBulk.length})
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center text-sm"
                    >
                      <Trash2 className="mr-2" size={16} />
                      Supprimer ({selectedMatieresForBulk.length})
                    </button>
                  </>
                )}
                <button
                  onClick={() => setActiveTab('ajouter')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center text-sm"
                >
                  <PlusCircle className="mr-2" size={16} />
                  Nouvelle Matière
                </button>
              </div>
            </div>

            {/* Panel d'édition en masse */}
            {showBulkEditPanel && selectedMatieresForBulk.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Modification en masse ({selectedMatieresForBulk.length} matière(s) sélectionnée(s))</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                    <select
                      value={bulkEditNiveau}
                      onChange={(e) => setBulkEditNiveau(e.target.value)}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">-- Ne pas modifier --</option>
                      {niveauxOptions.map(niveau => (
                        <option key={niveau.value} value={niveau.value}>{niveau.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={bulkEditType}
                      onChange={(e) => setBulkEditType(e.target.value)}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">-- Ne pas modifier --</option>
                      {typeOptions.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={bulkEditStatus}
                      onChange={(e) => setBulkEditStatus(e.target.value)}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">-- Ne pas modifier --</option>
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={handleBulkEdit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Save className="mr-2" size={16} />
                    Appliquer les modifications
                  </button>
                  <button
                    onClick={() => {
                      setShowBulkEditPanel(false);
                      setBulkEditNiveau('');
                      setBulkEditType('');
                      setBulkEditStatus('');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Liste des matières */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-semibold">
                  Liste Complète des Matières
                </h3>
                <div className="flex items-center space-x-4 mt-2">
                  <button
                    onClick={handleSelectAllMatieres}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {selectedMatieresForBulk.length === filteredMatieres.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                  <span className="text-sm text-gray-500">
                    {filteredMatieres.length} matière(s) trouvée(s)
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex space-x-1">
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Français</span>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Arabe</span>
                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">Commun</span>
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                    <Globe size={10} />
                  </span>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Actif</span>
                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Inactif</span>
                </div>
              </div>
            </div>

            {!schoolId ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 mb-2">Veuillez sélectionner une école pour voir les matières.</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Chargement des matières...</p>
              </div>
            ) : filteredMatieres.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Search className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 mb-2">Aucune matière trouvée.</p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Effacer la recherche
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedMatieresForBulk.length === filteredMatieres.length && filteredMatieres.length > 0}
                          onChange={handleSelectAllMatieres}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Niveau</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMatieres.map(matiere => {
                      const typeColor = matiere.type === 'arabe' ? 'bg-green-100 text-green-800' : 
                                      matiere.type === 'commun' ? 'bg-purple-100 text-purple-800' : 
                                      'bg-blue-100 text-blue-800';
                      const typeLabel = matiere.type === 'arabe' ? 'Arabe' : 
                                      matiere.type === 'commun' ? 'Commun' : 'Français';
                      
                      const niveauColor = matiere.niveau === 'tous' ? 'bg-gray-100 text-gray-800' :
                                        matiere.niveau === 'lycée' ? 'bg-purple-100 text-purple-800' :
                                        matiere.niveau === 'collège' ? 'bg-blue-100 text-blue-800' :
                                        matiere.niveau === 'élémentaire' ? 'bg-green-100 text-green-800' :
                                        'bg-yellow-100 text-yellow-800';
                      const niveauLabel = matiere.niveau === 'tous' ? 'Tous niveaux' : 
                                        niveauxOptions.find(n => n.value === matiere.niveau)?.label || matiere.niveau;
                      
                      const statusColor = matiere.status === 'actif' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
                      const statusLabel = matiere.status === 'actif' ? 'Actif' : 'Inactif';
                      const StatusIcon = matiere.status === 'actif' ? Eye : EyeOff;
                      
                      return (
                        <tr key={matiere.id} className={`hover:bg-gray-50 ${matiere.status === 'inactif' ? 'bg-gray-50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedMatieresForBulk.includes(matiere.id)}
                              onChange={() => handleSelectMatiereForBulk(matiere.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{matiere.name}</div>
                            {matiere.schoolId && (
                              <div className="text-xs text-gray-500">ID: {matiere.id.substring(0, 8)}...</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${niveauColor}`}>
                              {matiere.niveau === 'tous' && <Globe size={10} className="inline mr-1" />}
                              {niveauLabel}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${typeColor}`}>
                              {typeLabel}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full flex items-center ${statusColor}`}>
                              <StatusIcon size={12} className="mr-1" />
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {matiere.description || <span className="text-gray-400 italic">Aucune description</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditMatiere(matiere)}
                                className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                                title="Modifier"
                                disabled={!canManage}
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleToggleMatiereStatus(matiere.id, matiere.status)}
                                className={`p-1 rounded ${
                                  matiere.status === 'actif' 
                                    ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50' 
                                    : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                }`}
                                title={matiere.status === 'actif' ? 'Désactiver' : 'Réactiver'}
                                disabled={!canManage}
                              >
                                {matiere.status === 'actif' ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                              <button
                                onClick={() => handleDeleteMatiere(matiere.id, matiere.name)}
                                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                                title="Supprimer"
                                disabled={!canManage}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Actions globales */}
            {selectedMatieresForBulk.length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <AlertCircle className="text-blue-600 mr-2" size={20} />
                    <span className="text-blue-800 font-medium">
                      {selectedMatieresForBulk.length} matière(s) sélectionnée(s)
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setBulkEditStatus('inactif');
                        handleBulkToggleStatus();
                      }}
                      className="px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm flex items-center"
                    >
                      <EyeOff size={14} className="mr-1" />
                      Désactiver
                    </button>
                    <button
                      onClick={() => {
                        setBulkEditStatus('actif');
                        handleBulkToggleStatus();
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center"
                    >
                      <Eye size={14} className="mr-1" />
                      Réactiver
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Supprimer
                    </button>
                    <button
                      onClick={() => setSelectedMatieresForBulk([])}
                      className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ONGLET 2: AJOUTER/MODIFIER UNE MATIERE */}
      {activeTab === 'ajouter' && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="max-w-2xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">
                {isEditingMatiere ? 'Modifier une Matière' : 'Ajouter une Nouvelle Matière'}
              </h3>
              {isEditingMatiere && (
                <button
                  onClick={resetMatiereForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center text-sm"
                >
                  <X className="mr-2" size={16} />
                  Annuler
                </button>
              )}
            </div>
            
            <form onSubmit={handleSaveMatiere} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la Matière *
                </label>
                <input
                  type="text"
                  value={newMatiereName}
                  onChange={(e) => setNewMatiereName(e.target.value)}
                  placeholder="Ex: Mathématiques, Arabe, Coran, Sciences..."
                  className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!schoolId}
                />
                {!schoolId && (
                  <p className="text-sm text-red-500 mt-1">Veuillez sélectionner une école d'abord.</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau Scolaire
                  </label>
                  <select
                    value={newMatiereNiveau}
                    onChange={(e) => setNewMatiereNiveau(e.target.value)}
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!schoolId}
                  >
                    {niveauxOptions.map(niveau => (
                      <option key={niveau.value} value={niveau.value}>
                        {niveau.icon || ''} {niveau.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de Matière
                  </label>
                  <select
                    value={newMatiereType}
                    onChange={(e) => setNewMatiereType(e.target.value)}
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!schoolId}
                  >
                    {typeOptions.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={newMatiereStatus}
                    onChange={(e) => setNewMatiereStatus(e.target.value)}
                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!schoolId}
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnelle)
                </label>
                <textarea
                  value={newMatiereDescription}
                  onChange={(e) => setNewMatiereDescription(e.target.value)}
                  placeholder="Description de la matière, objectifs pédagogiques..."
                  rows="4"
                  className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!schoolId}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                {isEditingMatiere && (
                  <button
                    type="button"
                    onClick={resetMatiereForm}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    disabled={!schoolId}
                  >
                    Annuler
                  </button>
                )}
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={!schoolId || !newMatiereName.trim()}
                >
                  {isEditingMatiere ? (
                    <>
                      <Save className="mr-2" size={20} />
                      Modifier la Matière
                    </>
                  ) : (
                    <>
                      <PlusCircle className="mr-2" size={20} />
                      Ajouter la Matière
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Informations importantes</h4>
              <ul className="space-y-2 text-sm text-blue-700">
                <li>• <span className="font-medium">Statut "Inactif"</span> : La matière ne sera pas disponible pour attribution aux classes</li>
                <li>• <span className="font-medium">Niveau "Tous les niveaux"</span> : La matière sera disponible pour toutes les classes</li>
                <li>• <span className="font-medium">Modification de nom</span> : Si vous modifiez le nom, vérifiez qu'il n'existe pas déjà</li>
                <li>• <span className="font-medium">Suppression</span> : Une matière ne peut être supprimée que si elle n'est pas utilisée</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ONGLET 3: ATTRIBUTION AUX CLASSES */}
      {activeTab === 'liaisons' && (
        <div className="space-y-6">
          {/* En-tête */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Attribution des Matières aux Classes</h3>
                <p className="text-sm text-gray-600">
                  Sélectionnez une classe et attribuez-lui des matières avec leurs coefficients
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => { setActiveTab('ajouter'); resetMatiereForm(); }}
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center text-sm disabled:border-gray-400 disabled:text-gray-400 disabled:cursor-not-allowed"
                  disabled={!schoolId}
                >
                  <PlusCircle className="mr-2" size={16} />
                  Nouvelle Matière
                </button>
                
                <select
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  className="border p-2 rounded min-w-[250px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!schoolId || classes.length === 0}
                >
                  <option value="">-- Sélectionner une classe --</option>
                  {classes.map(classe => (
                    <option key={classe.id} value={classe.id}>
                      {classe.nom} {classe.niveau ? `(${classe.niveau})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {!schoolId ? (
            <div className="text-center py-12 bg-white rounded-lg shadow border">
              <Link size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Veuillez sélectionner une école</p>
              <p className="text-sm text-gray-500">
                Choisissez une école pour commencer l'attribution des matières aux classes.
              </p>
            </div>
          ) : selectedClassId ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Liste des Matières Disponibles */}
              <div className="lg:col-span-1 bg-white p-4 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-gray-800">
                    Matières Disponibles (Actives)
                  </h4>
                  <div className="flex space-x-1">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">F</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">A</span>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">C</span>
                    <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                      <Globe size={10} />
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {getFilteredMatieres().length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Aucune matière disponible pour cette classe.</p>
                      <button
                        onClick={() => setActiveTab('ajouter')}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Ajouter une matière
                      </button>
                    </div>
                  ) : (
                    getFilteredMatieres().map(matiere => {
                      const alreadyLinked = classMatieres.some(cm => 
                        cm.matiereId === matiere.id
                      );
                      
                      const typeColor = matiere.type === 'arabe' ? 'bg-green-100 text-green-800 border-green-200' : 
                                      matiere.type === 'commun' ? 'bg-purple-100 text-purple-800 border-purple-200' : 
                                      'bg-blue-100 text-blue-800 border-blue-200';
                      
                      const niveauIcon = matiere.niveau === 'tous' ? <Globe size={12} className="inline mr-1" /> : null;
                      
                      return (
                        <div 
                          key={matiere.id} 
                          className={`p-3 rounded border transition-colors ${
                            alreadyLinked 
                              ? 'bg-green-50 border-green-200' 
                              : `hover:bg-gray-50 ${typeColor}`
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <div className="font-medium">{matiere.name}</div>
                              <div className="flex items-center mt-1 space-x-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs flex items-center ${
                                  matiere.niveau === 'tous' ? 'bg-gray-100 text-gray-800' :
                                  matiere.niveau === 'lycée' ? 'bg-purple-100 text-purple-800' :
                                  matiere.niveau === 'collège' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {niveauIcon}
                                  {matiere.niveau === 'tous' ? 'Tous niveaux' : matiere.niveau}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${typeColor}`}>
                                  {matiere.type === 'arabe' ? 'Arabe' : 
                                   matiere.type === 'commun' ? 'Commun' : 'Français'}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => !alreadyLinked && handleAddMatiereToClass(matiere)}
                              disabled={alreadyLinked || matiere.status === 'inactif'}
                              className={`ml-2 px-3 py-1 rounded text-sm transition-colors ${
                                alreadyLinked 
                                  ? 'bg-green-100 text-green-800 cursor-default' 
                                  : matiere.status === 'inactif'
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {alreadyLinked ? (
                                <>
                                  <Check className="inline mr-1" size={14} />
                                  Ajoutée
                                </>
                              ) : matiere.status === 'inactif' ? 'Inactive' : 'Ajouter'}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Matières de la Classe */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
                <div className="bg-gray-100 px-6 py-4 border-b">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-gray-800">
                        Matières attribuées à la classe
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {classes.find(c => c.id === selectedClassId)?.nom || 'Classe'} 
                        • {classMatieres.length} matière(s)
                        {classes.find(c => c.id === selectedClassId)?.niveau && (
                          <span className="ml-2 text-gray-500">
                            (Niveau: {classes.find(c => c.id === selectedClassId)?.niveau})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium">Type</span>
                      <span className="text-sm font-medium">Coefficient</span>
                      <span className="text-sm font-medium">Note sur</span>
                    </div>
                  </div>
                </div>
                
                {classMatieres.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Settings size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="mb-2">Aucune matière attribuée à cette classe.</p>
                    <p className="text-sm">Sélectionnez des matières dans la liste de gauche.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {classMatieres.map(liaison => {
                      const typeColor = liaison.matiereType === 'arabe' ? 
                        'bg-green-100 text-green-800 border-green-200' : 
                        liaison.matiereType === 'commun' ? 
                        'bg-purple-100 text-purple-800 border-purple-200' : 
                        'bg-blue-100 text-blue-800 border-blue-200';
                      
                      return (
                        <div key={liaison.id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <div className="flex items-start">
                                <div className="flex-1">
                                  <div className="flex items-center mb-2">
                                    <h5 className="font-semibold text-lg mr-3">{liaison.matiereName}</h5>
                                    <span className={`px-2 py-1 rounded text-xs ${typeColor}`}>
                                      {liaison.matiereType === 'arabe' ? 'Arabe' : 
                                       liaison.matiereType === 'commun' ? 'Commun' : 'Français'}
                                    </span>
                                    {liaison.niveau && (
                                      <span className="ml-2 px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                                        {liaison.niveau === 'tous' ? (
                                          <>
                                            <Globe size={10} className="inline mr-1" />
                                            Tous niveaux
                                          </>
                                        ) : liaison.niveau}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {liaison.description && (
                                      <span className="text-gray-500">
                                        {liaison.description}
                                      </span>
                                    )}
                                    {liaison.schoolId && (
                                      <div className="mt-1 text-xs text-gray-400">
                                        ID École: {liaison.schoolId.substring(0, 8)}...
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveLiaison(liaison.id, liaison.matiereName)}
                                  className="ml-4 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                  title="Retirer"
                                >
                                  <Unlink size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Type de Matière
                              </label>
                              <select
                                value={liaison.matiereType || 'français'}
                                onChange={(e) => handleUpdateMatiereType(liaison.id, e.target.value, liaison.matiereName)}
                                className="border p-2 rounded w-full"
                              >
                                {typeOptions.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Coefficient
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0.5"
                                  step="0.5"
                                  value={coefficientInputs[liaison.id] || 1}
                                  onChange={(e) => {
                                    setCoefficientInputs(prev => ({
                                      ...prev,
                                      [liaison.id]: e.target.value
                                    }));
                                  }}
                                  onBlur={(e) => handleUpdateCoefficient(liaison.id, e.target.value)}
                                  className="border p-2 rounded w-24"
                                />
                                <span className="text-sm text-gray-500">
                                  (défaut: 1)
                                </span>
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Note sur
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="10"
                                  step="5"
                                  value={noteSurInputs[liaison.id] || 20}
                                  onChange={(e) => {
                                    setNoteSurInputs(prev => ({
                                      ...prev,
                                      [liaison.id]: e.target.value
                                    }));
                                  }}
                                  onBlur={(e) => handleUpdateNoteSur(liaison.id, e.target.value)}
                                  className="border p-2 rounded w-24"
                                />
                                <span className="text-sm text-gray-500">
                                  (défaut: 20)
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow border">
              <Link size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Veuillez sélectionner une classe</p>
              <p className="text-sm text-gray-500">
                Choisissez une classe dans la liste déroulante ci-dessus pour commencer l'attribution des matières.
              </p>
              {classes.length === 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-700 text-sm">Aucune classe disponible. Créez d'abord des classes.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatieresManagement;